// src/components/Home/HomeFormsTable.jsx
import React, { memo, useCallback, useMemo } from 'react';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

import HomeFormActionsMenu from './HomeFormActionsMenu';

/**
 * HomeFormsTable
 * -----------------------------------------------------------------------------
 * Google Sheets / Drive-style list view for Home forms.
 *
 * PURPOSE
 *  • Render the same form objects used by the Home grid view in a compact,
 *    scannable list/table layout.
 *  • Keep table/list presentation separate from HomePagetest data loading,
 *    pagination, screenshot watchers, and view-mode state.
 *  • Avoid duplicating HomeCard's full action menu logic until we extract a
 *    shared HomeFormActionsMenu component.
 *
 * ENTERPRISE RULES
 *  • No Firestore reads.
 *  • No screenshot URL resolution.
 *  • No pagination ownership.
 *  • No local mutation of form data.
 *  • Uses shared HomeFormActionsMenu for row actions so grid and list views
 *    stay aligned to the same permission/action schema.
 *  • Accepts optional renderRowActions override for future custom surfaces,
 *    but defaults to the shared enterprise action menu.
 *
 * EXPECTED INPUT
 *  forms: Array of form objects already fetched/enriched by HomePagetest.
 *
 * FUTURE EXTENSION
 *  Once the list/table UI is approved, HomeCard should also be migrated to
 *  use <HomeFormActionsMenu /> so both grid and list views share one menu
 *  implementation.
 * -----------------------------------------------------------------------------
 */

const DEFAULT_COLUMNS = Object.freeze([
  {
    id: 'name',
    label: 'Name',
    width: 'auto',
    align: 'left',
  },
  {
    id: 'status',
    label: 'Status',
    width: 120,
    align: 'left',
    hideOnXs: true,
  },
  {
    id: 'type',
    label: 'Type',
    width: 120,
    align: 'left',
    hideOnXs: true,
  },
  {
    id: 'modified',
    label: 'Modified',
    width: 150,
    align: 'right',
    hideOnXs: true,
  },
]);

const getTimestampMillis = (value) => {
  if (!value) return null;

  // Firestore Timestamp
  if (typeof value.toMillis === 'function') {
    try {
      return value.toMillis();
    } catch (_) {
      return null;
    }
  }

  // Firestore Timestamp-like plain object
  if (typeof value.seconds === 'number') {
    return value.seconds * 1000;
  }

  // JS Date
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }

  // ISO string / date string / numeric timestamp
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
};

const formatDate = (value) => {
  const ms = getTimestampMillis(value);
  if (!ms) return '—';

  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(ms));
  } catch (_) {
    return '—';
  }
};

const getBestModifiedDate = (form) => (
  form?.lastModified ||
  form?.updatedAt ||
  form?.screenshotUpdatedAt ||
  form?.publishedAt ||
  form?.savedAt ||
  form?.createdAt ||
  null
);

const getFormTitle = (form) => (
  form?.title ||
  form?.formDetails?.title ||
  'Untitled Form'
);

const getFormDescription = (form) => (
  form?.description ||
  form?.formDetails?.description ||
  'No description available'
);

const getFormId = (form) => (
  form?.id ||
  form?.formId ||
  null
);

const isPublishedForm = (form) => (
  form?.status === 'published' &&
  Boolean(getFormId(form))
);



const getFormTypeLabel = (form) => {
  if (form?.isTemplate) return 'Template';
  if (form?.isAssetUploadForm || form?.formType === 'assetUpload') {
    return 'Asset upload';
  }

  return 'Form';
};

const getStatusLabel = (status) => {
  if (!status) return 'Unknown';

  return String(status)
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getStatusChipSx = (status) => {
  const normalized = String(status || '').toLowerCase();

  if (normalized === 'published') {
    return {
      bgcolor: 'rgba(46, 125, 50, 0.08)',
      color: 'success.main',
      borderColor: 'rgba(46, 125, 50, 0.20)',
    };
  }

  if (normalized === 'draft') {
    return {
      bgcolor: 'rgba(117, 117, 117, 0.08)',
      color: 'text.secondary',
      borderColor: 'rgba(117, 117, 117, 0.20)',
    };
  }

  if (normalized === 'archived') {
    return {
      bgcolor: 'rgba(211, 47, 47, 0.08)',
      color: 'error.main',
      borderColor: 'rgba(211, 47, 47, 0.20)',
    };
  }

  return {
    bgcolor: 'rgba(25, 118, 210, 0.08)',
    color: 'primary.main',
    borderColor: 'rgba(25, 118, 210, 0.20)',
  };
};

const HomeFormsTable = memo(function HomeFormsTable({
  forms = [],
  onOpenForm = null,
  onDelete = null,
  renderRowActions = null,
  showActions = true,
  columns = DEFAULT_COLUMNS,
}) {
  const navigate = useNavigate();
  const safeForms = Array.isArray(forms) ? forms : [];

  // Enterprise:
  // - Show an actions column by default so the row shape stays stable.
  // - HomeFormActionsMenu will render null for users who cannot edit a form.
  // - Consumers can still pass renderRowActions for custom enterprise
  //   surfaces without changing the table internals.
  const hasActions = showActions === true;

  const handleOpenPublishedForm = useCallback((form) => {
    if (!isPublishedForm(form)) return;

    if (typeof onOpenForm === 'function') {
      onOpenForm(form);
      return;
    }

    const formId = getFormId(form);
    navigate(`/forms/${formId}`);
  }, [navigate, onOpenForm]);

  const visibleColumns = useMemo(() => (
    Array.isArray(columns) && columns.length > 0
      ? columns
      : DEFAULT_COLUMNS
  ), [columns]);

  if (safeForms.length === 0) {
    return (
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
          px: 2,
          py: 3,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No forms match this view.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden',
      }}
    >
      <Table
        size="small"
        aria-label="Recent forms list"
        sx={{
          tableLayout: 'fixed',
          minWidth: {
            xs: 0,
            sm: 760,
          },
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              height: 40,
              bgcolor: 'background.paper',
              '& th': {
                borderBottom: '1px solid',
                borderColor: 'divider',
              },
            }}
          >
            {visibleColumns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align || 'left'}
                sx={{
                  width: column.width || 'auto',
                  display: column.hideOnXs
                    ? { xs: 'none', sm: 'table-cell' }
                    : 'table-cell',
                  color: 'text.secondary',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                  py: 1,
                }}
              >
                {column.label}
              </TableCell>
            ))}

            {hasActions && (
              <TableCell
                align="right"
                aria-label="Actions"
                sx={{
                  width: 48,
                  color: 'text.secondary',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                  px: 1,
                  py: 1,
                }}
              />
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {safeForms.map((form) => {
            const formId = getFormId(form);
            const title = getFormTitle(form);
            const description = getFormDescription(form);
            const typeLabel = getFormTypeLabel(form);
            const statusLabel = getStatusLabel(form?.status);
            const modifiedLabel = formatDate(getBestModifiedDate(form));
            const canOpenForm = isPublishedForm(form);

            return (
              <TableRow
                key={formId || title}
                hover
                sx={{
                  height: 56,
                  cursor: 'default',
                  '& td': {
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  },
                  '&:last-of-type td': {
                    borderBottom: 'none',
                  },
                }}
              >
                <TableCell
                  sx={{
                    minWidth: 0,
                    py: 1,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Tooltip title={title} placement="top">
                      <Box
                        component={canOpenForm ? 'button' : 'span'}
                        type={canOpenForm ? 'button' : undefined}
                        onClick={(event) => {
                          if (!canOpenForm) return;

                          event.preventDefault();
                          event.stopPropagation();
                          handleOpenPublishedForm(form);
                        }}
                        sx={{
                          appearance: 'none',
                          border: 0,
                          background: 'transparent',
                          p: 0,
                          m: 0,
                          minWidth: 0,
                          maxWidth: '100%',
                          display: 'block',
                          textAlign: 'left',
                          cursor: canOpenForm ? 'pointer' : 'default',
                          color: canOpenForm ? 'text.primary' : 'text.primary',
                          '&:hover .HomeFormsTable-titleText': {
                            color: canOpenForm ? 'primary.main' : 'text.primary',
                          },
                          '&:focus-visible': {
                            outline: canOpenForm ? '2px solid' : 'none',
                            outlineColor: 'primary.main',
                            outlineOffset: 2,
                            borderRadius: 1,
                          },
                        }}
                      >
                        <Typography
                          className="HomeFormsTable-titleText"
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: 'inherit',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.35,
                            transition: 'color 0.15s ease',
                          }}
                        >
                          {title}
                        </Typography>
                      </Box>
                    </Tooltip>

                    <Tooltip title={description} placement="bottom">
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: {
                            xs: 'block',
                            sm: 'none',
                          },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          lineHeight: 1.3,
                          mt: 0.25,
                        }}
                      >
                        {description}
                      </Typography>
                    </Tooltip>
                  </Box>
                </TableCell>

                <TableCell
                  sx={{
                    display: { xs: 'none', sm: 'table-cell' },
                    py: 1,
                  }}
                >
                  <Chip
                    label={statusLabel}
                    variant="outlined"
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.70rem',
                      fontWeight: 500,
                      borderRadius: '999px',
                      ...getStatusChipSx(form?.status),
                    }}
                  />
                </TableCell>

                <TableCell
                  sx={{
                    display: { xs: 'none', sm: 'table-cell' },
                    py: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {typeLabel}
                  </Typography>
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    display: { xs: 'none', sm: 'table-cell' },
                    py: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {modifiedLabel}
                  </Typography>
                </TableCell>

                {hasActions && (
                  <TableCell
                    align="right"
                    onMouseDown={(event) => {
                      // Prevent table row focus/click from reacting to the
                      // action button before the menu receives the event.
                      event.stopPropagation();
                    }}
                    onClick={(event) => {
                      // Prevent row open from firing when action menu is clicked.
                      event.stopPropagation();
                    }}
                    sx={{
                      width: 48,
                      px: 1,
                      py: 0.75,
                    }}
                  >
                    {typeof renderRowActions === 'function' ? (
                      renderRowActions(form)
                    ) : (
                      <HomeFormActionsMenu
                        form={form}
                        onDelete={onDelete}
                        iconButtonSx={{
                          mr: -0.5,
                        }}
                      />
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}, areEqual);

function areEqual(prevProps, nextProps) {
  return (
    prevProps.forms === nextProps.forms &&
    prevProps.onOpenForm === nextProps.onOpenForm &&
    prevProps.onDelete === nextProps.onDelete &&
    prevProps.renderRowActions === nextProps.renderRowActions &&
    prevProps.showActions === nextProps.showActions &&
    prevProps.columns === nextProps.columns
  );
}

export default HomeFormsTable;